/**
 * Where a tool does its work, shown as a small badge under the tool title.
 *
 * Almost every tool runs entirely in the visitor's browser. The exceptions are listed here by id, with the
 * plain reason, so the badge never overstates. When a tool is added that talks to our server or downloads
 * a processing engine, add it here (tool-runtime.test.ts checks the ids are real tools).
 */
export type RuntimeKind = "device" | "engine" | "lookup" | "server";

export interface ToolRuntime {
  kind: RuntimeKind;
  label: string;
  detail: string;
  /** Replaces the generic "Files never stored" badge for tools where that line alone would be incomplete. */
  privacyBadge?: string;
}

const ENGINE_NOTE = "Your files are processed on your device. The first use downloads a processing engine, which your browser then keeps.";

/** Tools that load the video/audio engine or the text-recognition engine on first use. */
const ENGINE_TOOLS = [
  "audio-trimmer", "audio-merger", "audio-converter", "audio-compressor", "audio-normalizer", "audio-speed-changer",
  "audio-scrambler", "silence-remover", "extract-audio", "noise-remover", "ringtone-maker", "volume-booster", "voice-recorder",
  "compress-video", "convert-video", "trim-video", "merge-video", "video-to-gif", "thumbnail-gen", "subtitle-adder",
  "mute-video", "rotate-video", "video-speed-changer", "reverse-video", "ocr-tool", "ocr-pdf",
];

/** Tools that take typed values, not files: the badge says "Nothing you enter is stored" rather than "Files never stored". */
export const NON_FILE_TOOLS = new Set(["percentage-calculator", "tip-calculator", "age-calculator", "password-generator", "case-converter", "base64", "currency-converter"]);
export const NON_FILE_BADGE = "Nothing you enter is stored";

const SPECIAL: Record<string, ToolRuntime> = {
  "currency-converter": { kind: "lookup", label: "Live rates from our server", detail: "Only the exchange rates are fetched. The amounts you type stay on your device." },
  "og-preview": { kind: "server", label: "Page lookup uses our server", detail: "The web address you enter is sent to our server so it can read that page's preview details. Nothing is stored." },
  "yt-thumbnail": { kind: "lookup", label: "Fetches the thumbnail online", detail: "The thumbnail images are fetched from YouTube by your browser. Nothing is uploaded." },
  "book-writing-toolbox": {
    kind: "server",
    label: "Preview and export use our server",
    privacyBadge: "Draft stays in your browser",
    detail:
      "Your draft is written and saved in your browser only. Preview, Pre-flight Check and export send your chapters, images and book details to our server, which passes them to our typesetting service to build the PDF or EPUB. They are processed in memory for that one request and not kept. The only thing we store is a file you separately choose to pay to save.",
  },
};

export function toolRuntime(id: string): ToolRuntime {
  if (SPECIAL[id]) return SPECIAL[id];
  if (ENGINE_TOOLS.includes(id)) return { kind: "engine", label: "Runs on your device", detail: ENGINE_NOTE };
  return { kind: "device", label: "Runs on your device", detail: "Everything is done in your browser. Nothing you enter or open is uploaded." };
}

export const RUNTIME_SPECIAL_IDS = [...Object.keys(SPECIAL), ...ENGINE_TOOLS];
